import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { TABLE_PAGINATION_KEY } from "../constants/tablePaginationConstant";
import axios from "axios"
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"

dayjs.extend(utc);

const TOKEN_KEY = 'jwt';

export const privateLogin = () => {
    localStorage.setItem(TOKEN_KEY, 'TestLogin');
}

export const privateLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
}

export const isLogin = () => {
    if (localStorage.getItem(TOKEN_KEY) && localStorage.getItem("subDomain") !== 'Deny') {
        return true;
    }

    return false;
}

// Decode the JWT access token and return the logged-in user's id (null if unavailable)
export const getUserIdFromToken = () => {
    try {
        const token = localStorage.getItem("token");
        if (!token) return null;

        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.user_id || payload.sub || payload.id;
    } catch (error) {
        console.error("Error decoding token:", error);
        return null;
    }
}

export const startOfDay = (start_date) => {
    return moment(new Date(start_date)).startOf('day').utc().toISOString();
}

export const endOfDay = (end_date) => {
    return moment(new Date(end_date)).endOf('day').utc().toISOString();
}

export const formatStartDate = (start_date, tz) => {
    if (tz === "utc") {
        return dayjs(start_date).startOf('day').format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    } else {
        return dayjs(start_date).startOf('day').utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }   
}

export const formatEndDate = (end_date, tz) => {
    if (tz === "utc") {
        return dayjs(end_date).endOf('day').format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    } else {
        return dayjs(end_date).endOf('day').utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }   
}

// =================== formik related utils ==================== //

/**
 * Provides an error message if the input field is touched and has an error message associated with the specified key.
 * @param touchObject Contains the status of whether the input field has been touched by the user.
 * @param errorObject Contains error messages for incorrect input.
 * @param key The object key to check in both of the provided objects.
 * @returns {string} Return the empty string if no error else returns error message.
 */
export const getErrorMessage = (touchObject,errorObject,key) =>{
    if (touchObject[key]) {
      return errorObject[key];
    }
    return "";
}

/**
 * Checks if the input field with the specified key has been touched and has an associated error message.
 * @param touchObject Contains the status of whether the input field has been touched by the user.
 * @param errorObject Contains error messages for incorrect input.
 * @param key The object key to check in both provided objects.
 * @returns {boolean} True if the field has been touched and has an error message; otherwise, false.
 */
export const getError = (touchObject,errorObject,key) =>{
    if (touchObject[key]) {
      return errorObject[key] ? true : false;
    }
    return false;
};

/**
 * Retrieves data from the localStorage using the specified key.
 * @param {string} key - The key used to retrieve data from localStorage.
 * @returns {*} Returns the parsed data if the key exists; otherwise, returns null.
 * Example:
 * const savedData = getFromLocalStorage('userData');
 * console.log(savedData); // Output: { name: 'John', age: 30 }
 */
export const getFromLocalStorage = (key) => {
  try {
    const data = localStorage.getItem(key);

    if (data === null) {
      return null; // Return null if key doesn't exist
    }

    let parsedData;
    try {
      parsedData = JSON.parse(data); // Attempt to parse JSON data
    } catch (error) {
      // If parsing fails, return the original data (treated as string)
      return data;
    }

    if (typeof parsedData === "object") {
       return parsedData; // Return parsed data if it's an object or array
    } else {
       return parsedData; // Return parsed data (treated as string) if not an object
    }
    
  } catch (error) {
    console.error(`Error getting data from localStorage for key ${key}:`, error);
    return null; // Return null in case of errors
  }
};

/**
 * Stores the provided data in localStorage using the specified key.
 * @param {string} key - The key to use when storing data in localStorage.
 * @param {*} data - The data to be stored in localStorage.
 * Example:
 * const userData = { name: 'John', age: 30 };
 * setToLocalStorage('userData', userData);
 */
export const setToLocalStorage = (key, data) => {
  try {
    const jsonData = typeof data === "object" ? JSON.stringify(data) : data; // Convert data to JSON string
    localStorage.setItem(key, jsonData); // Store in localStorage
  } catch (error) {
    console.error(`Error setting data in localStorage for key ${key}:`, error);
    return null;
  }
};

/**
 * Retrieves table pagination configuration based on the provided route name.
 * This function fetches the rowsPerPage and page settings from local storage
 * and provides defaults if they are not found in the configuration.
 * @param {string} routeName - The routeName for the required table pagination config.
 * @returns {Object} An object containing rowsPerPage, page and activeStatus settings.
 * Example:
 * const paginationConfig = getTablePaginationConfiguration('dashboard');
 * Returns: { rowsPerPage: 10, page: 0, activeStatus: "active" }
 */
export const getTablePaginationConfiguration = routeName => {
  // Retrieve the table pagination configuration from localStorage
  const tablePaginationConfig = getFromLocalStorage(TABLE_PAGINATION_KEY) ?? {};

  // Get the rowsPerPage based on routeName from config or assign a default of 10
  const rowsPerPage = tablePaginationConfig[routeName]?.rowsPerPage ?? 10;

  // Get the page based on routeName from config or assign a default of 0
  const page = tablePaginationConfig[routeName]?.page ?? 0;

  // Get the status based on routeName from config or assign a default as "active"
  const activeStatus =
    tablePaginationConfig[routeName]?.activeStatus ?? "active";

  //Get the start date based on routeName from config or assign empty
  const startDate = tablePaginationConfig[routeName]?.startDate ?? new Date();

  //Get the end date based on routeName from config or assign empty
  const endDate = tablePaginationConfig[routeName]?.endDate ?? new Date();

  return {
    rowsPerPage,
    page,
    activeStatus,
    startDate,
    endDate,
  };
};

/**
 * Updates the table pagination configuration in local storage with the provided
 * rowsPerPage and page for a specific routeName.
 * @param {Object.rowsPerPage} - The number of rows per page to be stored.
 * @param {Object.page} - The current page index to be stored.
 * @param {Object.routeName} - The routeName for which pagination settings need to be updated.
 * @param {Object.activeStatus} - The activeStatus for which pagination settings need to be updated
 * Example:
 * handleTablePaginationSync({ rowsPerPage: 20, page: 1, routeName: 'dashboard' });
 * => Updates the pagination settings for the 'dashboard' route to display 20 rows per page and set the current page to 1.
 * @returns {void} Returns nothing.
 */
export const handleTablePaginationSync = ({rowsPerPage, page, routeName, activeStatus, startDate, endDate}) => {

  // console.log({rowsPerPage, page,activeStatus, routeName, startDate, endDate}, "from set")
  // Check routeName is missing or not
  if (!routeName) return;

  // Retrieve the existing table pagination configuration from localStorage
  const tablePaginationConfig = getFromLocalStorage(TABLE_PAGINATION_KEY) ?? {};

  // Update the configuration for the provided routeName with new rowsPerPage and page values
  tablePaginationConfig[routeName] = {
    rowsPerPage: rowsPerPage ?? tablePaginationConfig[routeName]?.rowsPerPage,
    page: page ?? tablePaginationConfig[routeName]?.page,
    activeStatus: activeStatus ?? tablePaginationConfig[routeName]?.activeStatus ?? "active",
    startDate: startDate ?? tablePaginationConfig[routeName]?.startDate,
    endDate: endDate ?? tablePaginationConfig[routeName]?.endDate
  };

  // Save the updated configuration back to localStorage
  setToLocalStorage(TABLE_PAGINATION_KEY, tablePaginationConfig);
};

/**
 * Generate the unique id using time stamp and uuidv4
 * @returns Unique Id
 */
export const generateUniqueId = () => {
  const timestamp = Date.now().toString() ; //gets the current timestamp
  const uuidV4 = uuidv4(); //generating unique id

  return uuidV4 + timestamp;
}

/**
 * function to fetch image data from URL and convert to base64
 * @param {String} url Url of the image 
 * @returns image
 */
export const getImageData = async url => {
  try {
    const response = await axios.get(url, {
      responseType: "arraybuffer",
    });
    const base64 = Buffer.from(response.data, "binary").toString("base64");
    return `data:image/jpeg;base64,${base64}`;
  } catch (error) {
    console.error("Error fetching image:", error);
    return "";
  }
};

export const checkDomainIsCaptis = () => {
  if (window.location.hostname?.split(".")?.[1] === 'captispay') {
    return true;
  }
  return false;
}

export const adjustTxnImagesSequence = (txnImages) => {
  if (txnImages.length === 1 && txnImages[0].includes("after_txn")) {
    return ["", txnImages[0]];
  }
  return txnImages;
}

/**
 * Convert local datetime string to UTC ISO string
 * @param {string} localDateTimeString - Local datetime in format YYYY-MM-DDTHH:mm
 * @returns {string} UTC ISO string
 */
export const convertLocalToUTC = (localDateTimeString) => {
  if (!localDateTimeString) return "";
  try {
    // Parse the local datetime string and convert to UTC
    return dayjs(localDateTimeString).utc().toISOString();
  } catch (error) {
    console.error("Error converting local time to UTC:", error);
    return "";
  }
};

/**
 * Convert UTC ISO string to local datetime string
 * @param {string} utcISOString - UTC datetime in ISO format
 * @returns {string} Local datetime in format YYYY-MM-DDTHH:mm
 */
export const convertUTCToLocal = (utcISOString) => {
  if (!utcISOString) return "";
  try {
    // Parse UTC string and convert to local time in datetime-local format
    return dayjs(utcISOString).format("YYYY-MM-DDTHH:mm");
  } catch (error) {
    console.error("Error converting UTC time to local:", error);
    return "";
  }
};

/**
 * Convert UTC ISO string to local display format
 * @param {string} utcISOString - UTC datetime in ISO format
 * @param {string} format - Display format (default: "MMM DD, YYYY hh:mm A")
 * @returns {string} Formatted local datetime string
 */
export const formatUTCToLocalDisplay = (utcISOString, format = "MMM DD, YYYY hh:mm A") => {
  if (!utcISOString) return "";
  try {
    return dayjs(utcISOString).format(format);
  } catch (error) {
    console.error("Error formatting UTC time to local display:", error);
    return "";
  }
};